import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as api from '../../services/api';
import type { TrendsParams } from '../../services/api';
import type {
  TrendsRevenueResponse,
  TrendsOperationsResponse,
  TrendsItemsResponse,
  TrendsCallsResponse,
  TrendsCustomersResponse,
  TrendsPersonnelResponse,
} from '../../types';

interface TrendsState {
  revenue: TrendsRevenueResponse | null;
  operations: TrendsOperationsResponse | null;
  items: TrendsItemsResponse | null;
  calls: TrendsCallsResponse | null;
  customers: TrendsCustomersResponse | null;
  personnel: TrendsPersonnelResponse | null;
  loading: boolean;
  error: string | null;
}

const initialState: TrendsState = {
  revenue: null,
  operations: null,
  items: null,
  calls: null,
  customers: null,
  personnel: null,
  loading: false,
  error: null,
};

export const fetchTrendsRevenue = createAsyncThunk(
  'trends/fetchRevenue',
  async (params: TrendsParams) => {
    const response = await api.fetchTrendsRevenue(params);
    return response.data;
  }
);

export const fetchTrendsOperations = createAsyncThunk(
  'trends/fetchOperations',
  async (params: TrendsParams) => {
    const response = await api.fetchTrendsOperations(params);
    return response.data;
  }
);

export const fetchTrendsItems = createAsyncThunk(
  'trends/fetchItems',
  async (params: TrendsParams) => {
    const response = await api.fetchTrendsItems(params);
    return response.data;
  }
);

export const fetchTrendsCalls = createAsyncThunk(
  'trends/fetchCalls',
  async (params: TrendsParams) => {
    const response = await api.fetchTrendsCalls(params);
    return response.data;
  }
);

export const fetchTrendsCustomers = createAsyncThunk(
  'trends/fetchCustomers',
  async (params: TrendsParams) => {
    const response = await api.fetchTrendsCustomers(params);
    return response.data;
  }
);

export const fetchTrendsPersonnel = createAsyncThunk(
  'trends/fetchPersonnel',
  async (params: TrendsParams) => {
    const response = await api.fetchTrendsPersonnel(params);
    return response.data;
  }
);

const trendsSlice = createSlice({
  name: 'trends',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Revenue
    builder
      .addCase(fetchTrendsRevenue.pending, (state) => { state.loading = true; })
      .addCase(fetchTrendsRevenue.fulfilled, (state, action) => {
        state.revenue = action.payload;
        state.loading = false;
      })
      .addCase(fetchTrendsRevenue.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch revenue trends';
      })
    // Operations
      .addCase(fetchTrendsOperations.fulfilled, (state, action) => {
        state.operations = action.payload;
      })
    // Items
      .addCase(fetchTrendsItems.fulfilled, (state, action) => {
        state.items = action.payload;
      })
    // Calls
      .addCase(fetchTrendsCalls.fulfilled, (state, action) => {
        state.calls = action.payload;
      })
    // Customers
      .addCase(fetchTrendsCustomers.fulfilled, (state, action) => {
        state.customers = action.payload;
      })
    // Personnel
      .addCase(fetchTrendsPersonnel.fulfilled, (state, action) => {
        state.personnel = action.payload;
      });
  },
});

export default trendsSlice.reducer;
